import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Send, Link as LinkIcon, MessageCircle, Trash2, EyeOff, Eye, Pin, PinOff,
  ChevronDown, ChevronUp, User, Hash, Lock, Users as UsersIcon, Plus, X,
} from "lucide-react";
import { Input } from "./ui/input";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const REACTION_LABEL = { amen: "Amen", prayer: "Prayer", fire: "Fire", praise: "Praise", heart: "Heart" };
const REACTION_ORDER = ["amen", "prayer", "fire", "praise", "heart"];

const timeAgo = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const Avatar = ({ path, name, size = 40 }) => {
  const src = path ? `${BACKEND}/api/files/${path}` : "";
  if (!src) return (
    <div style={{ width: size, height: size }} className="rounded-full bg-muted ring-1 ring-secondary/30 flex items-center justify-center shrink-0">
      <User size={size * 0.5} className="text-muted-foreground" />
    </div>
  );
  return <img src={src} alt={name || "user"} style={{ width: size, height: size }} className="rounded-full object-cover ring-1 ring-secondary/30 shrink-0" />;
};

// Render body with @mentions and #hashtags styled + click filter
function RichBody({ body, onHashtag }) {
  const parts = [];
  const re = /(@[A-Za-z][A-Za-z0-9_.\-]{1,40}|#[A-Za-z][A-Za-z0-9_]{1,40})/g;
  let last = 0; let m;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) parts.push({ t: "txt", v: body.slice(last, m.index) });
    if (m[0].startsWith("#")) parts.push({ t: "tag", v: m[0] });
    else parts.push({ t: "mention", v: m[0] });
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push({ t: "txt", v: body.slice(last) });
  return (
    <span>
      {parts.map((p, i) => {
        if (p.t === "tag") return (
          <button key={i} onClick={() => onHashtag?.(p.v.slice(1))} className="text-secondary hover:text-foreground transition-colors" data-testid="hashtag">
            {p.v}
          </button>
        );
        if (p.t === "mention") return <span key={i} className="text-primary" data-testid="mention">{p.v}</span>;
        return <span key={i}>{p.v}</span>;
      })}
    </span>
  );
}

export default function CommunityFeed({ moderationMode = false }) {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");
  const openComment = searchParams.get("comment");
  const [posts, setPosts] = useState([]);
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null); // null=all/mine, "network", or channel_id
  const [activeHashtag, setActiveHashtag] = useState(null);
  const [audiences, setAudiences] = useState(["Everyone"]);
  const [reactions, setReactions] = useState(REACTION_ORDER);
  const [hashtags, setHashtags] = useState([]);
  const [form, setForm] = useState({ body: "", audience: "Everyone", link: "", channel_id: "" });
  const [posting, setPosting] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const timerRef = useRef(null);

  const isAdmin = user?.is_admin;
  const canManageChannels = isAdmin || user?.role === "church_leader";
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadChannels = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/community/channels`, auth);
      setChannels(data);
    } catch { /* */ }
    // eslint-disable-next-line
  }, [token]);

  const loadHashtags = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/community/hashtags`, auth);
      setHashtags(data);
    } catch { /* */ }
    // eslint-disable-next-line
  }, [token]);

  const loadPosts = useCallback(async () => {
    try {
      const params = {};
      if (activeChannel) params.channel_id = activeChannel;
      if (activeHashtag) params.hashtag = activeHashtag;
      const { data } = await axios.get(`${API}/community/posts`, { ...auth, params });
      setPosts(data);
    } catch { /* silent for polling */ }
    // eslint-disable-next-line
  }, [token, activeChannel, activeHashtag]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/community/audiences`, auth)
      .then((r) => { setAudiences(r.data.audiences); setReactions(r.data.reactions); })
      .catch(() => {});
    loadChannels();
    loadHashtags();
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    if (!token) return;
    loadPosts();
    timerRef.current = setInterval(loadPosts, 30000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line
  }, [token, activeChannel, activeHashtag]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.body.trim()) return;
    setPosting(true);
    try {
      const body = { ...form };
      if (activeChannel && activeChannel !== "network" && !body.channel_id) body.channel_id = activeChannel;
      await axios.post(`${API}/community/posts`, body, auth);
      setForm({ body: "", audience: form.audience, link: "", channel_id: form.channel_id });
      toast.success("Posted.");
      loadPosts();
      loadHashtags();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Post failed.");
    } finally {
      setPosting(false);
    }
  };

  const react = async (pid, kind) => {
    setPosts((old) => old.map((p) => {
      if (p.id !== pid) return p;
      const has = p.my_reactions.includes(kind);
      const next = { ...p, reaction_counts: { ...p.reaction_counts } };
      next.reaction_counts[kind] = Math.max(0, (next.reaction_counts[kind] || 0) + (has ? -1 : 1));
      next.my_reactions = has ? p.my_reactions.filter((k) => k !== kind) : [...p.my_reactions, kind];
      return next;
    }));
    try { await axios.post(`${API}/community/posts/${pid}/react`, { kind }, auth); }
    catch { toast.error("Reaction failed."); loadPosts(); }
  };

  const removePost = async (p) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/community/posts/${p.id}`, auth);
      setPosts((old) => old.filter((x) => x.id !== p.id));
      toast.success("Deleted.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed."); }
  };
  const toggleHide = async (p) => {
    try {
      const { data } = await axios.post(`${API}/community/posts/${p.id}/hide`, {}, auth);
      setPosts((old) => old.map((x) => (x.id === p.id ? { ...x, hidden: data.hidden } : x)));
    } catch (err) { toast.error(err?.response?.data?.detail || "Action failed."); }
  };
  const togglePin = async (p) => {
    try {
      const { data } = await axios.post(`${API}/community/posts/${p.id}/pin`, {}, auth);
      setPosts((old) => old.map((x) => (x.id === p.id ? { ...x, pinned: data.pinned } : x)));
      loadPosts();
    } catch (err) { toast.error(err?.response?.data?.detail || "Action failed."); }
  };

  const joinChannel = async (c) => {
    try {
      await axios.post(`${API}/community/channels/${c.id}/join`, {}, auth);
      toast.success(`Joined ${c.name}.`);
      loadChannels();
    } catch (err) { toast.error(err?.response?.data?.detail || "Join failed."); }
  };
  const leaveChannel = async (c) => {
    try {
      await axios.post(`${API}/community/channels/${c.id}/leave`, {}, auth);
      toast.success(`Left ${c.name}.`);
      loadChannels();
      if (activeChannel === c.id) setActiveChannel(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Leave failed."); }
  };

  const visible = moderationMode ? posts : posts.filter((p) => !p.hidden);
  const activeChannelObj = channels.find((c) => c.id === activeChannel);

  return (
    <div data-testid="community-feed" className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8 max-w-6xl">
      {/* Channels + trending sidebar */}
      <aside className="lg:sticky lg:top-6 space-y-6 self-start" data-testid="community-sidebar">
        <div className="border border-border">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <p className="overline text-secondary text-[0.6rem]">Channels</p>
            {canManageChannels && (
              <button onClick={() => setShowManage((s) => !s)} className="text-muted-foreground hover:text-secondary" data-testid="toggle-manage-channels">
                <Plus size={14} />
              </button>
            )}
          </div>
          <ChannelButton
            label="Network Feed"
            active={activeChannel === null}
            onClick={() => { setActiveChannel(null); setActiveHashtag(null); }}
            testid="channel-all"
          />
          <ChannelButton
            label="Network Only"
            small
            active={activeChannel === "network"}
            onClick={() => { setActiveChannel("network"); setActiveHashtag(null); }}
            testid="channel-network"
          />
          {channels.map((c) => (
            <div key={c.id} className={`flex items-center border-t border-border ${activeChannel === c.id ? "bg-muted/40" : ""}`}>
              <button
                onClick={() => { setActiveChannel(c.id); setActiveHashtag(null); }}
                className={`flex-1 flex items-center gap-2 px-4 py-2.5 text-left transition-colors ${activeChannel === c.id ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`channel-${c.id}`}
              >
                {c.is_private ? <Lock size={12} className="text-primary" /> : <UsersIcon size={12} />}
                <span className="font-body text-sm flex-1 truncate">{c.name}</span>
                <span className="overline text-secondary text-[0.55rem]">{c.member_count}</span>
              </button>
              {c.is_member ? (
                <button onClick={() => leaveChannel(c)} title="Leave" className="px-2 text-muted-foreground hover:text-primary" data-testid={`leave-${c.id}`}>
                  <X size={12} />
                </button>
              ) : !c.is_private ? (
                <button onClick={() => joinChannel(c)} title="Join" className="px-3 overline text-[0.55rem] text-secondary hover:text-foreground" data-testid={`join-${c.id}`}>
                  Join
                </button>
              ) : null}
            </div>
          ))}
          {showManage && canManageChannels && (
            <ChannelCreator token={token} onCreated={loadChannels} onClose={() => setShowManage(false)} />
          )}
        </div>

        {hashtags.length > 0 && (
          <div className="border border-border" data-testid="trending-hashtags">
            <p className="overline text-secondary text-[0.6rem] px-4 py-3 border-b border-border">Trending</p>
            <div className="p-3 flex flex-wrap gap-1.5">
              {hashtags.slice(0, 12).map((h) => (
                <button
                  key={h.tag}
                  onClick={() => { setActiveHashtag(h.tag); setActiveChannel(null); }}
                  className={`overline text-[0.6rem] px-2.5 py-1 border transition-colors ${activeHashtag === h.tag ? "bg-secondary text-background border-secondary" : "border-border text-muted-foreground hover:text-secondary hover:border-secondary"}`}
                  data-testid={`trending-${h.tag}`}
                >
                  #{h.tag} <span className="opacity-60">{h.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </aside>

      {/* Feed */}
      <div>
        {activeHashtag && (
          <div className="mb-4 flex items-center gap-3 border-b border-border pb-3">
            <Hash size={16} className="text-secondary" />
            <span className="font-display uppercase text-xl text-foreground">{activeHashtag}</span>
            <button onClick={() => setActiveHashtag(null)} className="ml-auto overline text-[0.6rem] text-primary hover:text-foreground" data-testid="clear-hashtag">
              Clear
            </button>
          </div>
        )}
        {activeChannelObj && (
          <div className="mb-4 flex items-center gap-3 border-b border-border pb-3">
            {activeChannelObj.is_private ? <Lock size={14} className="text-primary" /> : <UsersIcon size={14} className="text-secondary" />}
            <span className="font-display uppercase text-xl text-foreground">{activeChannelObj.name}</span>
            {activeChannelObj.description && (
              <span className="font-editorial italic text-sm text-muted-foreground truncate">— {activeChannelObj.description}</span>
            )}
          </div>
        )}

        {!moderationMode && (
          <Composer
            user={user}
            token={token}
            form={form}
            setForm={setForm}
            audiences={audiences}
            channels={channels.filter((c) => c.is_member || isAdmin)}
            currentChannel={activeChannel && activeChannel !== "network" ? activeChannel : ""}
            onSubmit={submit}
            posting={posting}
          />
        )}

        {visible.length === 0 && (
          <div className="border border-border p-14 text-center" data-testid="community-empty">
            <p className="font-editorial italic text-2xl text-muted-foreground">
              {activeHashtag ? `No posts with #${activeHashtag} yet.` : activeChannelObj ? "No posts in this channel yet." : "The feed is quiet. Start the conversation."}
            </p>
          </div>
        )}

        <div className="space-y-5">
          {visible.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              token={token}
              reactions={reactions}
              onReact={react}
              onDelete={removePost}
              onToggleHide={toggleHide}
              onTogglePin={togglePin}
              onHashtag={(t) => { setActiveHashtag(t); setActiveChannel(null); }}
              isAdmin={isAdmin}
              currentUserId={user?.id}
              highlight={highlightId === p.id}
              autoOpenComments={highlightId === p.id && !!openComment}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ChannelButton({ label, active, onClick, testid, small }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left px-4 py-2.5 border-t border-border transition-colors ${small ? "text-xs" : ""} ${active ? "bg-muted/40 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
    >
      <span className="font-body text-sm">{label}</span>
    </button>
  );
}

function ChannelCreator({ token, onCreated, onClose }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [priv, setPriv] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/community/channels`, { name, description: desc, is_private: priv }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Channel created.");
      onCreated?.();
      setName(""); setDesc(""); setPriv(false);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed.");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="border-t border-border p-3 space-y-2 bg-muted/20" data-testid="channel-creator">
      <Input
        placeholder="Channel name (e.g. ALU Students)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="bg-transparent border-0 border-b border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-primary h-9 px-0 text-sm"
        data-testid="new-channel-name"
      />
      <Input
        placeholder="Description (optional)"
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        className="bg-transparent border-0 border-b border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-primary h-9 px-0 text-xs"
        data-testid="new-channel-desc"
      />
      <label className="flex items-center gap-2 text-xs text-muted-foreground">
        <input type="checkbox" checked={priv} onChange={(e) => setPriv(e.target.checked)} data-testid="new-channel-private" />
        Private (invite-only)
      </label>
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className="overline text-[0.6rem] bg-primary text-primary-foreground px-3 py-1.5 hover:bg-primary/85 disabled:opacity-50" data-testid="create-channel-btn">
          {busy ? "Creating…" : "Create"}
        </button>
        <button type="button" onClick={onClose} className="overline text-[0.6rem] text-muted-foreground">Cancel</button>
      </div>
    </form>
  );
}

function Composer({ user, token, form, setForm, audiences, channels, currentChannel, onSubmit, posting }) {
  const [suggest, setSuggest] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [caretIdx, setCaretIdx] = useState(0);
  const textareaRef = useRef(null);

  const onBodyChange = async (e) => {
    const v = e.target.value;
    const pos = e.target.selectionStart;
    setForm({ ...form, body: v });
    // Detect @mention token near caret
    const upto = v.slice(0, pos);
    const m = upto.match(/@([A-Za-z][A-Za-z0-9_.\-]*)$/);
    if (m) {
      setCaretIdx(pos);
      try {
        const { data } = await axios.get(`${API}/community/users/search`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { q: m[1] },
        });
        setSuggest(data);
        setSuggestOpen(data.length > 0);
      } catch {
        setSuggestOpen(false);
      }
    } else {
      setSuggestOpen(false);
    }
  };

  const pickMention = (u) => {
    // Replace the @token before caret with @handle
    const before = form.body.slice(0, caretIdx).replace(/@([A-Za-z][A-Za-z0-9_.\-]*)$/, `@${u.handle}`);
    const after = form.body.slice(caretIdx);
    const next = `${before} ${after}`;
    setForm({ ...form, body: next });
    setSuggestOpen(false);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  return (
    <form onSubmit={onSubmit} className="border border-border bg-muted/10 p-5 mb-8 relative" data-testid="community-composer">
      <div className="flex gap-3 items-start">
        <Avatar path={user?.avatar_path} name={user?.name} size={40} />
        <div className="flex-1 min-w-0">
          <textarea
            ref={textareaRef}
            data-testid="composer-body"
            value={form.body}
            onChange={onBodyChange}
            placeholder="Share a word, a testimony, a scripture, a call to prayer… try @name or #hashtag"
            rows={3}
            maxLength={5000}
            className="w-full bg-transparent border-0 border-b border-white/10 focus:outline-none focus:border-primary text-foreground font-body p-2 resize-none"
          />
          {suggestOpen && (
            <div className="border border-secondary/40 bg-black shadow-xl divide-y divide-border max-h-52 overflow-y-auto" data-testid="mention-suggest">
              {suggest.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => pickMention(s)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted/40 transition-colors"
                  data-testid={`mention-${s.handle}`}
                >
                  <Avatar path={s.avatar_path} name={s.name} size={28} />
                  <div className="min-w-0">
                    <p className="font-body text-sm text-foreground truncate">{s.name}</p>
                    <p className="font-body text-xs text-muted-foreground truncate">@{s.handle} · {s.title || s.role}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-col md:flex-row md:items-center gap-3 mt-3">
            <select
              data-testid="composer-audience"
              value={form.audience}
              onChange={(e) => setForm({ ...form, audience: e.target.value })}
              className="bg-black border border-white/10 text-foreground overline text-[0.65rem] px-3 py-2 focus:outline-none focus:border-secondary"
            >
              {audiences.map((a) => <option key={a} value={a} className="bg-black">{a}</option>)}
            </select>
            <select
              data-testid="composer-channel"
              value={form.channel_id || currentChannel || ""}
              onChange={(e) => setForm({ ...form, channel_id: e.target.value })}
              className="bg-black border border-white/10 text-foreground overline text-[0.65rem] px-3 py-2 focus:outline-none focus:border-secondary"
            >
              <option value="" className="bg-black">Network (no channel)</option>
              {channels.map((c) => <option key={c.id} value={c.id} className="bg-black">{c.name}</option>)}
            </select>
            <div className="flex-1 flex items-center gap-2 border-b border-white/10 focus-within:border-primary">
              <LinkIcon size={14} className="text-muted-foreground shrink-0" />
              <Input
                data-testid="composer-link"
                placeholder="Optional link (https://…)"
                value={form.link}
                onChange={(e) => setForm({ ...form, link: e.target.value })}
                className="bg-transparent border-0 rounded-none focus-visible:ring-0 h-9 text-sm px-0 flex-1"
              />
            </div>
            <button
              type="submit"
              disabled={posting || !form.body.trim()}
              data-testid="composer-submit"
              className="flex items-center gap-2 bg-primary text-primary-foreground overline text-[0.65rem] px-5 py-3 hover:bg-primary/85 disabled:opacity-50 transition-colors"
            >
              <Send size={14} /> {posting ? "Posting…" : "Post"}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

function PostCard({ post, token, reactions, onReact, onDelete, onToggleHide, onTogglePin, onHashtag, isAdmin, currentUserId, highlight, autoOpenComments }) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [cbody, setCbody] = useState("");
  const [busy, setBusy] = useState(false);
  const [glow, setGlow] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (highlight && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      setGlow(true);
      const t = setTimeout(() => setGlow(false), 2600);
      return () => clearTimeout(t);
    }
  }, [highlight]);

  useEffect(() => { if (autoOpenComments) setShowComments(true); }, [autoOpenComments]);

  const loadComments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/community/posts/${post.id}/comments`, { headers: { Authorization: `Bearer ${token}` } });
      setComments(data);
    } catch { /* */ }
  }, [post.id, token]);

  useEffect(() => { if (showComments) loadComments(); }, [showComments, loadComments]);

  const addComment = async (e) => {
    e.preventDefault();
    if (!cbody.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/community/posts/${post.id}/comments`, { body: cbody }, { headers: { Authorization: `Bearer ${token}` } });
      setCbody("");
      loadComments();
    } catch (err) { toast.error(err?.response?.data?.detail || "Comment failed."); }
    finally { setBusy(false); }
  };

  const deleteComment = async (cid) => {
    if (!window.confirm("Delete comment?")) return;
    try {
      await axios.delete(`${API}/community/comments/${cid}`, { headers: { Authorization: `Bearer ${token}` } });
      loadComments();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed."); }
  };

  return (
    <article ref={cardRef} data-testid="community-post" data-postid={post.id} className={`border border-border bg-background p-5 transition-shadow duration-500 ${post.pinned ? "border-l-4 border-l-secondary" : ""} ${post.hidden ? "opacity-60" : ""} ${glow ? "ring-2 ring-secondary shadow-[0_0_40px_rgba(201,162,75,0.35)]" : ""}`}>
      <header className="flex items-start gap-3">
        <Avatar path={post.author_avatar_path} name={post.author_name} size={44} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-body text-sm text-foreground">{post.author_name || "—"}</p>
            {post.author_role && <span className="overline text-secondary text-[0.55rem]">{post.author_role}</span>}
            {post.audience !== "Everyone" && (
              <span className="overline text-primary text-[0.55rem] border border-primary/50 px-2 py-0.5">{post.audience}</span>
            )}
            {post.channel_name && (
              <span className="overline text-secondary text-[0.55rem] border border-secondary/50 px-2 py-0.5">#{post.channel_name}</span>
            )}
            {post.pinned && <span className="overline text-secondary text-[0.55rem]">· Pinned</span>}
            {post.hidden && <span className="overline text-primary text-[0.55rem]">· Hidden</span>}
          </div>
          <p className="font-body text-xs text-muted-foreground">{timeAgo(post.created_at)} ago</p>
        </div>

        {isAdmin && (
          <div className="flex items-center gap-1">
            <IconAction title={post.pinned ? "Unpin" : "Pin"} onClick={() => onTogglePin(post)} testid={`pin-${post.id}`}>
              {post.pinned ? <PinOff size={14} /> : <Pin size={14} />}
            </IconAction>
            <IconAction title={post.hidden ? "Unhide" : "Hide"} onClick={() => onToggleHide(post)} testid={`hide-${post.id}`}>
              {post.hidden ? <Eye size={14} /> : <EyeOff size={14} />}
            </IconAction>
          </div>
        )}
        {(isAdmin || post.author_id === currentUserId) && (
          <IconAction title="Delete" onClick={() => onDelete(post)} testid={`delete-${post.id}`}>
            <Trash2 size={14} />
          </IconAction>
        )}
      </header>

      <div className="mt-4 whitespace-pre-wrap font-body text-foreground text-[15px] leading-relaxed" data-testid="post-body">
        <RichBody body={post.body} onHashtag={onHashtag} />
      </div>
      {post.link && (
        <a href={post.link} target="_blank" rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-2 border border-border px-3 py-2 text-sm text-secondary hover:text-foreground hover:border-secondary transition-colors"
          data-testid="post-link">
          <LinkIcon size={14} /> {post.link.length > 60 ? `${post.link.slice(0, 60)}…` : post.link}
        </a>
      )}

      <div className="mt-4 flex items-center gap-2 flex-wrap border-t border-border pt-3">
        {reactions.map((k) => {
          const on = post.my_reactions.includes(k);
          const c = post.reaction_counts[k] || 0;
          return (
            <button key={k} onClick={() => onReact(post.id, k)} data-testid={`react-${k}-${post.id}`}
              className={`overline text-[0.6rem] px-3 py-1.5 border transition-colors ${on ? "bg-secondary text-background border-secondary" : "border-border text-muted-foreground hover:border-secondary hover:text-secondary"}`}>
              {REACTION_LABEL[k]} {c > 0 && <span className="ml-1">{c}</span>}
            </button>
          );
        })}
        <button onClick={() => setShowComments((s) => !s)} data-testid={`toggle-comments-${post.id}`}
          className="ml-auto overline text-[0.6rem] text-muted-foreground hover:text-secondary flex items-center gap-1">
          <MessageCircle size={12} /> {post.comments_count || 0} {(post.comments_count || 0) === 1 ? "comment" : "comments"}
          {showComments ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
      </div>

      {showComments && (
        <div className="mt-4 border-t border-border pt-4 space-y-3" data-testid={`comments-${post.id}`}>
          {comments.map((c) => (
            <div key={c.id} className={`flex items-start gap-3 ${c.hidden ? "opacity-60" : ""}`} data-testid="community-comment">
              <Avatar path={c.author_avatar_path} name={c.author_name} size={30} />
              <div className="flex-1 min-w-0">
                <p className="font-body text-xs text-secondary">
                  {c.author_name} <span className="text-muted-foreground">· {timeAgo(c.created_at)} ago</span>
                  {c.hidden && <span className="text-primary ml-2">· hidden</span>}
                </p>
                <p className="font-body text-sm text-foreground whitespace-pre-wrap">
                  <RichBody body={c.body} onHashtag={onHashtag} />
                </p>
              </div>
              {(isAdmin || c.author_id === currentUserId) && (
                <button onClick={() => deleteComment(c.id)} className="text-muted-foreground hover:text-primary" data-testid={`del-comment-${c.id}`}>
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          ))}
          <form onSubmit={addComment} className="flex items-center gap-3 pt-2" data-testid="comment-form">
            <Input value={cbody} onChange={(e) => setCbody(e.target.value)} placeholder="Reply… @name #hashtag" maxLength={2000}
              className="bg-transparent border-0 border-b border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-primary text-sm h-9 px-0 flex-1"
              data-testid="comment-input" />
            <button type="submit" disabled={busy || !cbody.trim()} data-testid="comment-submit"
              className="overline text-[0.6rem] bg-primary text-primary-foreground px-4 py-2 hover:bg-primary/85 disabled:opacity-50">
              Reply
            </button>
          </form>
        </div>
      )}
    </article>
  );
}

function IconAction({ children, onClick, title, testid }) {
  return (
    <button onClick={onClick} title={title} data-testid={testid}
      className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-secondary transition-colors">
      {children}
    </button>
  );
}
